/**
 * ホーム — 紐付いたブランド・入力した公式値の状況・使い方。
 *
 * ブランド（チェーン）の紐付けは 2 通り: 運営が承認時に入れる / メールのドメインが公式サイトと一致すれば
 * 事業者が自分で紐付ける（`POST /claim/domain`）。一致しない場合は運営へ連絡してもらう。
 */
import { useEffect, useState } from "react";
import { api, type ChainCandidate, type Changes, type LinkedChain, type Me, type MenuEntry, type OfficialRequestSummary } from "../api";
import { Link } from "../router";
import { REVIEW_STATE_LABEL } from "../format";

export function Home({ me }: { me: Me }) {
  const [chains, setChains] = useState<LinkedChain[] | null>(null);
  const [entries, setEntries] = useState<MenuEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const [c, e] = await Promise.all([api.chains(), api.entries()]);
    if (c.ok) setChains(c.data);
    else setError(c.error);
    if (e.ok) setEntries(e.data);
  };
  useEffect(() => {
    void load();
  }, []);

  const count = (state: MenuEntry["reviewState"]) => entries?.filter((x) => x.reviewState === state).length ?? 0;

  return (
    <section className="stack-lg">
      <div>
        <h1>{me.merchant.name}</h1>
        <p className="muted">kalori に掲載中の御社メニューを確認し、公式の栄養成分値を入力・申請できます。</p>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="card">
        <h2>紐付いているブランド</h2>
        {chains === null ? (
          <p className="muted">読み込んでいます…</p>
        ) : chains.length > 0 ? (
          <ul className="list">
            {chains.map((c) => (
              <li key={c.shopId} className="list-row">
                <span>
                  <strong>{c.name}</strong> <span className="muted small">{c.method === "operator" ? "運営が紐付け" : "メールのドメインで確認"}</span>
                </span>
                <Link href={`/catalog?shop=${encodeURIComponent(c.shopId)}`}>掲載中のメニューを見る</Link>
              </li>
            ))}
          </ul>
        ) : (
          <ClaimCandidates me={me} onLinked={() => void load()} />
        )}
      </div>

      {chains && chains.length > 0 && <OfficialRequests chains={chains} />}

      {chains && chains.length > 0 && <WeeklyChanges />}

      <div className="card">
        <h2>公式値の入力状況</h2>
        <div className="stats">
          {(["editing", "pending_review", "published"] as const).map((s) => (
            <Link key={s} href="/entries" className="stat">
              <span className="stat-value">{entries ? count(s) : "—"}</span>
              <span className="stat-label">{REVIEW_STATE_LABEL[s]}</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="card">
        <h2>使い方</h2>
        <ol className="steps">
          <li>
            <strong>掲載中のメニューを確認する</strong> — 項目ごとに「公式サイト」「AI 推定」など値の出どころを表示しています。
            値どうしが噛み合っていない品は <Link href="/catalog?attention=1">確認をおすすめ</Link> にまとめています。
          </li>
          <li>
            <strong>公式値を入力する</strong> — 直したいメニューを選び、御社の栄養成分値を入力します。気になる品だけで構いません。
          </li>
          <li>
            <strong>申請する</strong> — 運営が内容を確認し、承認の翌朝に kalori のカタログへ反映します。
          </li>
          <li>
            <strong>変わったら改訂する</strong> — 公開済みの値は「値を改訂する」から直して申請します。承認されるまでは、いまの値がそのまま使われます。
          </li>
        </ol>
        <p className="muted small">誤りのご指摘・削除のご相談は、いつでも無料で承ります（画面上部の「ご意見・ご要望」から）。</p>
      </div>
    </section>
  );
}

function ClaimCandidates({ me, onLinked }: { me: Me; onLinked: () => void }) {
  const [data, setData] = useState<{ candidates: ChainCandidate[]; emailDomain: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const canClaim = me.role === "owner" || me.role === "admin";

  useEffect(() => {
    void api.candidates().then((r) => r.ok && setData(r.data));
  }, []);

  const claim = async (shopIds: string[]) => {
    setBusy(true);
    const r = await api.claim(shopIds);
    setBusy(false);
    if (!r.ok) return setMessage(r.error);
    if (r.data.needsReview.length > 0) {
      setMessage("ほかのアカウントが紐付け済みのブランドがあります。運営が確認してご連絡します。");
    }
    if (r.data.linked.length > 0 || r.data.alreadyLinked.length > 0) onLinked();
  };

  if (!data) return <p className="muted">読み込んでいます…</p>;
  const claimable = data.candidates.filter((c) => c.state === "claimable");
  return (
    <div className="stack">
      <p>まだブランドが紐付いていません。</p>
      {data.candidates.length > 0 ? (
        <>
          <p className="muted small">
            メールのドメイン（{data.emailDomain}）が公式サイトと一致するブランドです。紐付けると、そのブランドの掲載メニューを確認・入力できます。
          </p>
          <ul className="list">
            {data.candidates.map((c) => (
              <li key={c.shopId} className="list-row">
                <strong>{c.name}</strong>
                <span className="muted small">{c.state === "needs_review" ? "運営の確認が必要です" : c.state === "linked" ? "紐付け済み" : ""}</span>
              </li>
            ))}
          </ul>
          {canClaim && claimable.length > 0 && (
            <button className="btn primary" disabled={busy} onClick={() => void claim(claimable.map((c) => c.shopId))}>
              {busy ? "紐付けています…" : `${claimable.length} ブランドを紐付ける`}
            </button>
          )}
          {!canClaim && <p className="muted small">紐付けはオーナー・管理者の方が行えます。</p>}
        </>
      ) : (
        <p className="muted small">
          メールのドメインから自動で確認できるブランドがありませんでした。運営が御社とブランドの関係を確認のうえ紐付けますので、ブランド名を添えて contact@kalbi.jp までご連絡ください。
        </p>
      )}
      {message && <p className="notice">{message}</p>}
    </div>
  );
}

/** この 1 週間の変化（件数だけ・一覧は「変化の知らせ」）。紐付いたブランドがあるときだけ読む */
function WeeklyChanges() {
  const [c, setC] = useState<Changes | null>(null);
  useEffect(() => {
    void api.changes(7).then((r) => r.ok && setC(r.data));
  }, []);
  if (!c) return null;
  const total = c.newWithoutOfficial.length + c.discontinued.length + c.nutritionChanged.length + c.pastAvailability.length;
  return (
    <div className="card">
      <div className="row-between">
        <h2>
          この 1 週間の変化 <span className="badge trial">試用</span>
        </h2>
        <Link href="/changes">変化の知らせを見る</Link>
      </div>
      {total === 0 ? (
        <p className="muted small">公式値に関わる変化はありませんでした。</p>
      ) : (
        <ul className="small">
          {c.newWithoutOfficial.length > 0 && <li>新しく載ったメニュー（公式値なし）: {c.newWithoutOfficial.length} 品</li>}
          {c.discontinued.length > 0 && <li>公式値があるメニューが見当たらなくなった: {c.discontinued.length} 品</li>}
          {c.nutritionChanged.length > 0 && <li>御社サイトの栄養成分の表記が変わった: {c.nutritionChanged.length} 品</li>}
          {c.pastAvailability.length > 0 && <li>提供期間を過ぎて公開中: {c.pastAvailability.length} 品</li>}
        </ul>
      )}
    </div>
  );
}

/**
 * 公式値を待っている声 — 利用者が推定値のメニューで「このお店に公式値を希望する」を押した件数。
 * 無料（試用ではない）: 公式値を出していただく理由そのものなので、参加の前から見えている（2026-10-07）。
 */
function OfficialRequests({ chains }: { chains: LinkedChain[] }) {
  const [s, setS] = useState<OfficialRequestSummary | null>(null);
  useEffect(() => {
    void api.officialRequests().then((r) => r.ok && setS(r.data));
  }, []);
  if (!s) return null;
  const nameOf = (shopId: string) => chains.find((c) => c.shopId === shopId)?.name ?? shopId;
  return (
    <div className="card stack">
      <h2>公式値を待っている声</h2>
      <p className="muted small">
        kalori のアプリと kalori.jp で、推定値のメニューに「このお店に公式値を希望する」が押された件数です（1 人 1 メニューにつき 1 件。kalori.jp は回線ごと）。毎朝更新します。
      </p>
      {s.chains.length === 0 ? (
        <p className="muted small">まだ届いていません。推定値のメニューを見た利用者が押すと、ここに件数が出ます。</p>
      ) : (
        <div className="stack">
          <div className="stats stats-wide">
            {s.chains.map((c) => (
              <Link key={c.shopId} href={`/catalog?shop=${encodeURIComponent(c.shopId)}`} className="stat">
                <span className="stat-value">{c.requests.toLocaleString("ja-JP")} 件</span>
                <span className="stat-label">
                  {nameOf(c.shopId)}（{c.products.toLocaleString("ja-JP")} 品）
                </span>
              </Link>
            ))}
          </div>
          <h3>希望の多いメニュー</h3>
          <ul className="list">
            {s.top.map((t) => (
              <li key={t.productId} className="list-row">
                <span>
                  <Link href={`/catalog/${t.productId}`}>{t.name}</Link>{" "}
                  <span className="muted small">
                    {chains.length > 1 && `${nameOf(t.shopId)} ・ `}
                    {t.requests.toLocaleString("ja-JP")} 件
                  </span>
                </span>
                {t.fulfilled ? (
                  <span className="muted small">公式値を掲載中</span>
                ) : (
                  <Link href={`/catalog/${t.productId}`}>公式値を入力する</Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
